import { c as _c } from "react/compiler-runtime";
import { Avatar, Badge, Button, Card, HStack, Text, VStack, Wrap } from '@chakra-ui/react';
import { COMP_OPTS } from '@src/components/sco/Advisors/data/becomeAdvisor';
import { WARM_INTRO_OPTIONS, type AboutYouValues, type AdvisorApplicationValues } from '@src/components/sco/Advisors/sections/advisorApplication/useAdvisorApplication';
import { ScoUser } from '@src/graphql-types';
import { ReactNode } from 'react';
import { LuArrowLeft, LuCheck } from 'react-icons/lu';
const ENGAGEMENT_LABEL_BY_KEY: Record<string, string> = {
  scheduled_calls: 'Scheduled calls',
  email: 'Email',
  chat: 'Chat / DMs',
  in_person: 'In person'
};
const Row = t0 => {
  const $ = _c(5);
  const {
    label,
    children
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <Text color="colorPalette.fg/60" textStyle="labelXs">{label}</Text>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <VStack align="stretch" gap={1}>{t1}{children}</VStack>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
const TagList = t0 => {
  const $ = _c(3);
  const {
    values,
    empty
  } = t0;
  let t1;
  if ($[0] !== empty || $[1] !== values) {
    t1 = values.length ? <Wrap gap={1.5}>{values.map(_temp)}</Wrap> : <Text color="colorPalette.fg/50" fontSize="sm">{empty}</Text>;
    $[0] = empty;
    $[1] = values;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};

/**
 * Founder-facing preview assembled from the application answers. Shown as an
 * interstitial after the last form step — not as a stepper tab.
 */
export const ReviewStep = ({
  values,
  aboutYou,
  currentUser
}: {
  values: AdvisorApplicationValues;
  aboutYou?: AboutYouValues | null;
  currentUser?: ScoUser | null;
}) => {
  const name = [aboutYou?.firstName, aboutYou?.lastName].filter(Boolean).join(' ').trim() || currentUser?.fullName || currentUser?.displayName || currentUser?.firstName || 'You';
  const pictureUrl = aboutYou?.pictureUrl ?? currentUser?.pictureUrl;
  const location = aboutYou?.locationTitle || currentUser?.location;
  const compLabels: string[] = values.compensationKeys.map(key => COMP_OPTS.find(option => option.key === key)?.label as string | undefined).filter((label): label is string => !!label);
  const engageLabels = values.engagementStyleKeys.map(key_0 => ENGAGEMENT_LABEL_BY_KEY[key_0] ?? key_0).filter(Boolean);
  const introLabels: string[] = values.warmIntroKeys.map(key_1 => WARM_INTRO_OPTIONS.find(option_0 => option_0.key === key_1)?.label as string | undefined).filter((label_0): label_0 is string => !!label_0);
  const experiences = values.experiences.map(experience => {
    const org = experience.organizationName.trim();
    const type = experience.experienceTypeKey.trim();
    if (!org) {
      return '';
    }
    return type && type !== 'job' ? `${org} · ${type}` : org;
  }).filter(Boolean);
  return <VStack align={'stretch'} gap={4}>
      <Card.Root variant={'subtle'}>
        <Card.Body>
          <VStack align={'stretch'} gap={5}>
            <HStack align={'start'} gap={4}>
              <Avatar.Root size={'lg'}>
                {pictureUrl ? <Avatar.Image src={pictureUrl} /> : null}
                <Avatar.Fallback name={name} />
              </Avatar.Root>
              <VStack align={'stretch'} gap={0.5} minWidth={0}>
                <Text fontSize={'lg'} fontWeight={'semibold'}>
                  {name}
                </Text>
                <Text color={'colorPalette.fg/70'} fontFamily={'longform'} fontSize={'sm'}>
                  {values.headline || 'Add a headline so founders know who you are.'}
                </Text>
                {location ? <Text color={'colorPalette.fg/55'} fontSize={'xs'}>
                    {location}
                  </Text> : null}
              </VStack>
            </HStack>

            <Row label={'Advises on'}>
              <TagList empty={'No areas picked yet.'} values={values.categories} />
            </Row>

            <Row label={'Skills'}>
              <TagList empty={'No skills picked yet.'} values={values.skills} />
            </Row>

            {experiences.length > 0 && <Row label={'Notable experiences'}>
                <Text fontSize={'sm'}>{experiences.join(' · ')}</Text>
              </Row>}

            <Row label={'Compensation'}>
              <TagList empty={'Not set.'} values={compLabels} />
            </Row>

            <Row label={'Engagement'}>
              <TagList empty={'Not set.'} values={engageLabels} />
            </Row>

            {introLabels.length > 0 && <Row label={'Warm intros'}>
                <TagList empty={''} values={introLabels} />
              </Row>}

            {values.note.trim() && <Row label={'Note to founders'}>
                <Text color={'colorPalette.fg/70'} fontFamily={'longform'} fontSize={'sm'}>
                  {values.note}
                </Text>
              </Row>}
          </VStack>
        </Card.Body>
      </Card.Root>
    </VStack>;
};

/** Apply-only confirmation screen after the four form steps. */
export const ApplicationReview = t0 => {
  const $ = _c(22);
  const {
    values,
    aboutYou,
    currentUser,
    error,
    saving,
    submitLabel,
    onBack,
    onSubmit
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <VStack align="stretch" gap={1}><Text fontSize="2xl" fontWeight="semibold">Review your application</Text><Text color="colorPalette.fg/60" fontFamily="longform" fontSize="sm">This is how you will appear to founders. Go back if you want to change anything.</Text></VStack>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== aboutYou || $[2] !== currentUser || $[3] !== values) {
    t2 = <ReviewStep aboutYou={aboutYou} currentUser={currentUser} values={values} />;
    $[1] = aboutYou;
    $[2] = currentUser;
    $[3] = values;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== error) {
    t3 = error ? <Text color="red.fg" fontFamily="longform" fontSize="sm">{error}</Text> : null;
    $[5] = error;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <LuArrowLeft />;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== onBack) {
    t5 = <Button variant="ghost" onClick={onBack}>{t4} Back</Button>;
    $[8] = onBack;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <LuCheck />;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== onSubmit || $[12] !== saving || $[13] !== submitLabel) {
    t7 = <Button loading={saving} variant="cta" onClick={onSubmit}>{t6} {submitLabel}</Button>;
    $[11] = onSubmit;
    $[12] = saving;
    $[13] = submitLabel;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t5 || $[16] !== t7) {
    t8 = <HStack gap={3} justify="space-between" wrap="wrap">{t5}{t7}</HStack>;
    $[15] = t5;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t2 || $[19] !== t3 || $[20] !== t8) {
    t9 = <VStack align="stretch" gap={5}>{t1}{t2}{t3}{t8}</VStack>;
    $[18] = t2;
    $[19] = t3;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
};
export type ApplicationSubmittedMode = 'live' | 'pending';

/** Post-submit confirmation, shared by the apply flow. */
export const ApplicationSubmitted = t0 => {
  const $ = _c(21);
  const {
    steps,
    actions,
    mode: t1,
    error
  } = t0;
  const mode = t1 === undefined ? "live" : t1;
  const t2 = mode === "pending" ? "orange.fg" : "green.fg";
  const t3 = mode === "pending" ? "Application submitted for review" : "You're live as an advisor";
  let t4;
  if ($[0] !== t2 || $[1] !== t3) {
    t4 = <Text color={t2} fontWeight="bold" textStyle="labelXs">{t3}</Text>;
    $[0] = t2;
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const t5 = mode === "pending" ? "Thanks \u2014 our team will review your profile before it goes live. Members on the $49+ plan skip this queue." : "Members on the $49+ plan go live immediately. Founders can find you in the directory and match you to open seats.";
  let t6;
  if ($[3] !== t5) {
    t6 = <Text color="colorPalette.fg/70" fontFamily="longform">{t5}</Text>;
    $[3] = t5;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== t4 || $[6] !== t6) {
    t7 = <VStack align="stretch" gap={2} textAlign="center">{t4}{t6}</VStack>;
    $[5] = t4;
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== steps) {
    t8 = steps.map(_temp8);
    $[8] = steps;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== t8) {
    t9 = <VStack align="stretch" gap={3} marginX="auto" maxWidth="md" width="full">{t8}</VStack>;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== error) {
    t10 = error ? <Text color="red.fg" fontFamily="longform" fontSize="sm" textAlign="center">{error}</Text> : null;
    $[12] = error;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== actions) {
    t11 = <HStack gap={3} justify="center" wrap="wrap">{actions}</HStack>;
    $[14] = actions;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t10 || $[17] !== t11 || $[18] !== t7 || $[19] !== t9) {
    t12 = <VStack align="stretch" gap={5} paddingY={6}>{t7}{t9}{t10}{t11}</VStack>;
    $[16] = t10;
    $[17] = t11;
    $[18] = t7;
    $[19] = t9;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  return t12;
};
function _temp(value) {
  return <Badge key={value} colorPalette="orange" variant="subtle">{value}</Badge>;
}
function _temp8(step) {
  return <HStack key={step.n} align="start" gap={3}><Badge colorPalette="green" variant="subtle">{step.n}</Badge><VStack align="start" gap={0}><Text fontSize="sm" fontWeight="semibold">{step.title}</Text><Text color="colorPalette.fg/60" fontFamily="longform" fontSize="xs">{step.desc}</Text></VStack></HStack>;
}